import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, Table, Modal, Alert } from "react-bootstrap";
import Pagination from "react-bootstrap/Pagination";
import Select from "react-select";
import { Link } from "react-router-dom";
import { useAccounts } from "../../contextjs/AccountContext";
import { useTransactions } from "../../contextjs/TransactionContext";
import { fetchStoreAccountSettings } from "../../api/api"
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Helper
const getExpenseAmount = (trx, excludeTillCharges = false) => {
  return trx.items
    .filter(
      it =>
        it.transaction_type === "DEBIT" &&
        it.account_details?.account_type_name === "Expense" &&
        !["Cost of Goods Sold", "Purchase"].includes(it.account_details.account_name) &&
        !(excludeTillCharges && it.account_details.account_name === "Till Transaction Charges")
    )
    .reduce((acc, it) => acc + parseFloat(it.amount || 0), 0);
};

const ExpenseEntry = () => {
  const { accounts } = useAccounts();
  const {
    transactions,      // current page's expense transactions
    totalCount, // total for server pagination
    createNewTransaction,
    loadTransactions
  } = useTransactions();

  // date filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [excludeTillCharges, setExcludeTillCharges] = useState(false);
  const [expenseAccountSettings, setExpenseAccountSettings] = useState([]);
  const [expensePaymentSettings, setExpensePaymentSettings] = useState([])

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 20;
  const totalPages = Math.ceil(totalCount / entriesPerPage);

  // new expense form
  const [showForm, setShowForm] = useState(false);
  // details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // local expense creation
  const [expense, setExpense] = useState({
    transaction_date: new Date().toISOString().split("T")[0],
    reference: "",
    description: "",
    payer_account: "",
    items: [{ account: "", item_description: "", amount: 0 }]
  });

  useEffect(() => {
    const loadAccountSettings = async () => {
      try {
        const [expenseSettings, paymentSettings] = await Promise.all([
          fetchStoreAccountSettings({ usage_type: "EXPENSE" }),
          fetchStoreAccountSettings({ usage_type: "EXPENSE_PAYMENT" }),
        ]);
        setExpenseAccountSettings(Array.isArray(expenseSettings) ? expenseSettings : []);
        setExpensePaymentSettings(Array.isArray(paymentSettings) ? paymentSettings : []);
      } catch (error) {
        console.error("Error loading store account settings:", error);
        setExpenseAccountSettings([]);
        setExpensePaymentSettings([]);
      }
    };

    loadAccountSettings();
  }, []);

  const enabledExpenseIds = new Set(
    expenseAccountSettings.filter((setting) => setting.is_enabled).map((setting) => String(setting.account))
  );

  const accountOptionFromSetting = (setting) => ({
    id: setting.account,
    account_name: setting.label_override || setting.account_name,
    account_type_details: {
      account_type_name: setting.account_type_name,
    },
  });

  // Payment accounts
  const basePaymentAccounts = accounts.filter(
    ac => ac.account_type_details?.account_type_name === "Asset"
  );
  const paymentAccounts = expensePaymentSettings.length
      ? expensePaymentSettings
          .filter((setting) => setting.is_enabled)
          .filter((setting) => setting.account_type_name === "Asset")
          .map(accountOptionFromSetting)
      : basePaymentAccounts;

  // Expense accounts
  const baseExpenseAccounts = accounts.filter(
    ac => ac.account_type_details.account_type_name === "Expense" &&
        !["Cost of Goods Sold", "Purchase"].includes(ac.account_name)
  );
  const expenseAccounts = expenseAccountSettings.length
      ? baseExpenseAccounts.filter((ac) => enabledExpenseIds.has(String(ac.id)))
      : baseExpenseAccounts;

  //** On changes to currentPage, date
  useEffect(() => {
    const filters = {expense_only: true, exclude_till_charges: excludeTillCharges};
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;

    loadTransactions(currentPage, entriesPerPage, filters);
  }, [currentPage, startDate, endDate, excludeTillCharges, entriesPerPage, loadTransactions]);

  //** New expense form logic
  const handleShowForm = () => setShowForm(true);
  const handleHideForm = () => setShowForm(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalDebit = expense.items.reduce((acc, it) => acc + parseFloat(it.amount || 0), 0);

    const transactionItems = [
      ...expense.items.map(it => ({
        account: it.account,
        item_description: it.item_description,
        amount: parseFloat(it.amount || 0),
        transaction_type: "DEBIT"
      }))
    ];

    transactionItems.push({
      account: expense.payer_account, // Payer Account
      item_description: expense.description,
      amount: totalDebit,
      transaction_type: "CREDIT"
    });

    const newTransaction = {
      transaction_date: expense.transaction_date,
      reference: expense.reference,
      description: expense.description,
      amount: totalDebit,
      usage_context: "EXPENSE",
      items: transactionItems
    };

    try {
      await createNewTransaction(newTransaction);
    } catch (error) {
      const data = error.response?.data;
      const message = data?.items || data?.detail || "Unable to create expense transaction.";
      alert(Array.isArray(message) ? message.join("") : message);
      return;
    }

    setExpense({
      transaction_date: new Date().toISOString().split("T")[0],
      reference: "",
      description: "",
      payer_account: "",
      items: [{ account: "", item_description: "", amount: 0 }]
    });
    handleHideForm();

    // optionally re-fetch the page + chart
    const filters = { expense_only: true, exclude_till_charges: excludeTillCharges };
    if (startDate) filters.start_date = startDate;
    if (endDate) filters.end_date = endDate;

    loadTransactions(currentPage, entriesPerPage, filters);
  };

  //** details
  const handleShowDetails = (trx) => {
    setSelectedTransaction(trx);
    setShowDetailsModal(true);
  };
  const handleCloseDetails = () => {
    setSelectedTransaction(null);
    setShowDetailsModal(false);
  };

  //** The table shows only the current page from “transactions”
  //   Filter out transactions that have 0 expense lines
  const filteredTransactions = transactions.filter(tx => getExpenseAmount(tx, excludeTillCharges) > 0);

  //** handle pagination
  const handlePageChange = (newPage) => {
    if (newPage !== currentPage && newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  //** Bounded pagination
  const maxLinks = 5;
  let startLink = Math.max(currentPage - 2, 1);
  let endLink = startLink + maxLinks - 1;
  if (endLink > totalPages) {
    endLink = totalPages;
    startLink = Math.max(endLink - (maxLinks - 1), 1);
  }
  const pageNumbersToShow = [];
  for (let p = startLink; p <= endLink; p++) {
    pageNumbersToShow.push(p);
  }

  return (
    <div className="container mt-5">
      <h1>Expenses</h1>

      <Button variant="primary" className="mb-4" onClick={handleShowForm}>
        New Expense
      </Button>

      {/* Date Filter */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group controlId="startDate">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={(e) => {setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="endDate">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={(e) => {setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end pb-2">
          <Form.Check
            type="switch"
            id="exclude-till-charges"
            label="Hide Till transaction charges"
            checked={excludeTillCharges}
            onChange={(e) => {
              setExcludeTillCharges(e.target.checked);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button as={Link} to="/accounting/budgets" variant="outline-primary" className="w-100">
            Open budgets & expense analysis
          </Button>
        </Col>
      </Row>

      <Alert variant="light" className="border">
        Dates apply automatically. Charts, account comparisons, ceilings and profit-based spending capacity are available on the Budgets page.
      </Alert>

      <h2>Expense Entries (Current Page)</h2>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Date</th>
            <th>Reference</th>
            <th>Description</th>
            <th>Payer Account</th>
            <th>Expense Amount</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.map((trx) => {
            const amt = getExpenseAmount(trx, excludeTillCharges);
            if (amt <= 0) return null;
            const payerLine = trx.items.find(
                (it) =>
                    it.transaction_type === "CREDIT" &&
                    it.account_details?.account_type_name !== "Expense"
            );
            // if (!payerLine) return null;

            // const allowedPayers = new Set([
            //     "Cash",
            //     "DTB Bank",
            //     "George Mpesa",
            //     "Kenya Commercial Bank (KCB)",
            //     "Equity Bank",
            //     "Mpesa Till Account"
            // ]);
            // if (!allowedPayers.has(payerLine.account_details.account_name)) return null;

            return (
              <tr key={trx.id}>
                <td>{new Date(trx.transaction_date).toLocaleDateString("en-GB")}</td>
                <td>{trx.reference}</td>
                <td>{trx.description}</td>
                <td>{payerLine?.account_details?.account_name || ""}</td>
                <td>{amt.toFixed(2)}</td>
                <td>
                  <Button variant="info" onClick={() => handleShowDetails(trx)}>
                    <FontAwesomeIcon icon={faEye} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Server-Side Pagination */}
      <Pagination className="justify-content-center mt-4">
        <Pagination.First
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {pageNumbersToShow.map(pg => (
          <Pagination.Item
            key={pg}
            active={pg === currentPage}
            onClick={() => handlePageChange(pg)}
          >
            {pg}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>

      {/* New Expense Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>New Expense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Similar form submission */}
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group controlId="payerAccount">
                  <Form.Label>Payer Account</Form.Label>
                  <Select
                    options={paymentAccounts.map(ac => ({
                      value: ac.id,
                      label: ac.account_name
                    }))}
                    value={
                      expense.payer_account
                        ? paymentAccounts.map(ac => ({ value: ac.id, label: ac.account_name }))
                            .find(opt => opt.value === expense.payer_account)
                        : null
                    }
                    onChange={(selected) => {
                      setExpense({ ...expense, payer_account: selected.value });
                    }}
                    placeholder="Select Payer Account"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="transactionDate">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={expense.transaction_date}
                    onChange={(e) => setExpense({ ...expense, transaction_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="reference">
                  <Form.Label>Reference</Form.Label>
                  <Form.Control
                    type="text"
                    value={expense.reference}
                    onChange={(e) => setExpense({ ...expense, reference: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    value={expense.description}
                    onChange={(e) => setExpense({ ...expense, description: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Table striped bordered hover className="mt-4">
              <thead>
                <tr>
                  <th>Expense Account</th>
                  <th>Particulars</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {expense.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Select
                        options={expenseAccounts.map(ac => ({
                          value: ac.id,
                          label: ac.account_name
                        }))}
                        value={
                          item.account
                            ? expenseAccounts.map(ac => ({ value: ac.id, label: ac.account_name }))
                                .find(opt => opt.value === item.account)
                            : null
                        }
                        onChange={(sel) => {
                          const newItems = [...expense.items];
                          newItems[index].account = sel.value;
                          setExpense({ ...expense, items: newItems });
                        }}
                        placeholder="Select Expense Account"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.item_description}
                        onChange={(e) => {
                          const newItems = [...expense.items];
                          newItems[index].item_description = e.target.value;
                          setExpense({ ...expense, items: newItems });
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.amount}
                        onChange={(e) => {
                          const newItems = [...expense.items];
                          newItems[index].amount = parseFloat(e.target.value || 0);
                          setExpense({ ...expense, items: newItems });
                        }}
                      />
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        onClick={() => {
                          const newItems = [...expense.items];
                          newItems.splice(index, 1);
                          setExpense({ ...expense, items: newItems });
                        }}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Button
              variant="primary"
              className="mt-3"
              onClick={() =>
                setExpense({
                  ...expense,
                  items: [...expense.items, { account: "", item_description: "", amount: 0 }]
                })
              }
            >
              + Add Line
            </Button>
            <Button
              variant="success"
              type="submit"
              className="mt-3 float-end"
            >
              Save Expense
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetails} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Expense Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <>
              <h5>
                Transaction Date:{" "}
                {new Date(selectedTransaction.transaction_date).toLocaleDateString("en-GB")}
              </h5>
              <h5>Reference: {selectedTransaction.reference}</h5>
              <h5>Description: {selectedTransaction.description}</h5>

              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Particulars</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTransaction.items.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it.account_details?.account_name}</td>
                      <td>{it.item_description}</td>
                      <td>{it.transaction_type === "DEBIT" ? it.amount : ""}</td>
                      <td>{it.transaction_type === "CREDIT" ? it.amount : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ExpenseEntry;
